/**
 * SDFGPUCompute.ts
 *
 * WebGPU compute shader accelerator for SDF (Signed Distance Field) CNC stock simulation.
 * Stores float distance values per voxel on GPU and performs tool removal via compute shaders.
 *
 * Key differences from VoxelGPUCompute.ts:
 * - Uses array<f32> storage (one float per voxel, NOT packed u32)
 * - Memory = resX * resY * resZ * 4 bytes
 * - Removal: sdfData[idx] = max(sdfData[idx], -toolSDF(pos))  [CSG difference]
 * - Non-atomic writes (safe with sub-region dispatch)
 *
 * Architecture:
 * - Sub-region AABB dispatch: CPU computes tool AABB, dispatches only affected voxels
 * - Batch mode: up to 256 swept segments per dispatch
 * - Grid uniform includes sub-region bounds (startX/Y/Z, sizeX/Y/Z)
 */

import { VoxelToolGeometry } from './VoxelCSGEngine';

const WGSL_SDF_SHADER = /* wgsl */`
struct Grid {
  resX: u32,
  resY: u32,
  resZ: u32,
  voxelSize: f32,

  originX: f32,
  originY: f32,
  originZ: f32,
  numPositions: u32,

  // Sub-region dispatch bounds
  startX: u32,
  startY: u32,
  startZ: u32,
  sizeX: u32,

  sizeY: u32,
  sizeZ: u32,
  totalSubVoxels: u32,
  workgroupsX: u32,
}

struct ToolGeom {
  dirX: f32,
  dirY: f32,
  dirZ: f32,
  _pad0: f32,

  diameter: f32,
  cornerRadius: f32,
  length: f32,
  toolType: u32, // 0=flat, 1=ball, 2=bull
}

@group(0) @binding(0) var<uniform> grid: Grid;
@group(0) @binding(1) var<uniform> tool: ToolGeom;
@group(0) @binding(2) var<storage, read_write> sdfData: array<f32>;
@group(0) @binding(3) var<storage, read> positions: array<f32>; // 6 floats per segment: ax,ay,az,bx,by,bz

// Utility: dot product
fn dot3(ax: f32, ay: f32, az: f32, bx: f32, by: f32, bz: f32) -> f32 {
  return ax * bx + ay * by + az * bz;
}

// Utility: length
fn length3(x: f32, y: f32, z: f32) -> f32 {
  return sqrt(x * x + y * y + z * z);
}

// Utility: clamp
fn clampf(v: f32, minVal: f32, maxVal: f32) -> f32 {
  return max(minVal, min(maxVal, v));
}

// SDF: Cylinder (infinite along axis)
fn sdCylinder(px: f32, py: f32, pz: f32, axisX: f32, axisY: f32, axisZ: f32, radius: f32) -> f32 {
  // Distance to axis
  let dp = dot3(px, py, pz, axisX, axisY, axisZ);
  let projX = axisX * dp;
  let projY = axisY * dp;
  let projZ = axisZ * dp;

  let perpX = px - projX;
  let perpY = py - projY;
  let perpZ = pz - projZ;

  let dist = length3(perpX, perpY, perpZ);
  return dist - radius;
}

// SDF: Sphere
fn sdSphere(px: f32, py: f32, pz: f32, cx: f32, cy: f32, cz: f32, radius: f32) -> f32 {
  let dx = px - cx;
  let dy = py - cy;
  let dz = pz - cz;
  return length3(dx, dy, dz) - radius;
}

// SDF: Capsule (line segment with radius)
fn sdCapsule(px: f32, py: f32, pz: f32, ax: f32, ay: f32, az: f32, bx: f32, by: f32, bz: f32, radius: f32) -> f32 {
  let pax = px - ax;
  let pay = py - ay;
  let paz = pz - az;

  let bax = bx - ax;
  let bay = by - ay;
  let baz = bz - az;

  let h = clampf(dot3(pax, pay, paz, bax, bay, baz) / dot3(bax, bay, baz, bax, bay, baz), 0.0, 1.0);

  let qx = pax - bax * h;
  let qy = pay - bay * h;
  let qz = paz - baz * h;

  return length3(qx, qy, qz) - radius;
}

// SDF: Torus (for bull nose)
fn sdTorus(px: f32, py: f32, pz: f32, axisX: f32, axisY: f32, axisZ: f32, majorRadius: f32, minorRadius: f32) -> f32 {
  // Project to torus plane perpendicular to axis
  let dp = dot3(px, py, pz, axisX, axisY, axisZ);
  let projX = axisX * dp;
  let projY = axisY * dp;
  let projZ = axisZ * dp;

  let perpX = px - projX;
  let perpY = py - projY;
  let perpZ = pz - projZ;

  let perpLen = length3(perpX, perpY, perpZ);
  let qx = perpLen - majorRadius;
  let qy = dp;

  return length3(qx, qy, 0.0) - minorRadius;
}

// Compute tool SDF at point p, for swept segment [a,b]
fn computeToolSDF(px: f32, py: f32, pz: f32, ax: f32, ay: f32, az: f32, bx: f32, by: f32, bz: f32) -> f32 {
  let radius = tool.diameter * 0.5;
  let dirX = tool.dirX;
  let dirY = tool.dirY;
  let dirZ = tool.dirZ;

  // Closest point on swept line
  let sweepX = bx - ax;
  let sweepY = by - ay;
  let sweepZ = bz - az;
  let sweepLen2 = sweepX * sweepX + sweepY * sweepY + sweepZ * sweepZ;

  var closestX = ax;
  var closestY = ay;
  var closestZ = az;

  if (sweepLen2 > 1e-6) {
    let pax = px - ax;
    let pay = py - ay;
    let paz = pz - az;
    let t = clampf(dot3(pax, pay, paz, sweepX, sweepY, sweepZ) / sweepLen2, 0.0, 1.0);
    closestX = ax + sweepX * t;
    closestY = ay + sweepY * t;
    closestZ = az + sweepZ * t;
  }

  // Relative position from closest tool center
  let relX = px - closestX;
  let relY = py - closestY;
  let relZ = pz - closestZ;

  var sdfVal: f32;

  if (tool.toolType == 0u) {
    // Flat end mill: cylinder + flat cap
    let cylinderSDF = sdCylinder(relX, relY, relZ, dirX, dirY, dirZ, radius);

    // Cap: distance to plane at tool tip
    let tipDist = dot3(relX, relY, relZ, dirX, dirY, dirZ);
    let capSDF = abs(tipDist) - tool.length * 0.5;

    sdfVal = max(cylinderSDF, capSDF);

  } else if (tool.toolType == 1u) {
    // Ball end mill: hemisphere + cylinder
    let cylinderSDF = sdCylinder(relX, relY, relZ, dirX, dirY, dirZ, radius);

    // Hemisphere at tip
    let tipX = dirX * tool.length * 0.5;
    let tipY = dirY * tool.length * 0.5;
    let tipZ = dirZ * tool.length * 0.5;
    let hemisphereSDF = sdSphere(relX, relY, relZ, tipX, tipY, tipZ, radius);

    sdfVal = min(cylinderSDF, hemisphereSDF);

  } else {
    // Bull nose: torus + cylinder
    let cornerR = tool.cornerRadius;
    let majorR = radius - cornerR;

    // Torus at corner
    let torusOffsetLen = tool.length * 0.5 - cornerR;
    let torusX = dirX * torusOffsetLen;
    let torusY = dirY * torusOffsetLen;
    let torusZ = dirZ * torusOffsetLen;

    let torusRelX = relX - torusX;
    let torusRelY = relY - torusY;
    let torusRelZ = relZ - torusZ;

    let torusSDF = sdTorus(torusRelX, torusRelY, torusRelZ, dirX, dirY, dirZ, majorR, cornerR);

    // Cylinder shaft
    let cylinderSDF = sdCylinder(relX, relY, relZ, dirX, dirY, dirZ, radius);
    let shaftClip = dot3(relX, relY, relZ, dirX, dirY, dirZ) + torusOffsetLen;

    sdfVal = min(torusSDF, max(cylinderSDF, -shaftClip));
  }

  return sdfVal;
}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let subIdx = gid.x + gid.y * grid.workgroupsX * 256u;

  if (subIdx >= grid.totalSubVoxels) {
    return;
  }

  // Map sub-region linear index to local coords
  let localZ = subIdx / (grid.sizeX * grid.sizeY);
  let rem = subIdx % (grid.sizeX * grid.sizeY);
  let localY = rem / grid.sizeX;
  let localX = rem % grid.sizeX;

  // Convert to global coords
  let x = grid.startX + localX;
  let y = grid.startY + localY;
  let z = grid.startZ + localZ;

  // Bounds check
  if (x >= grid.resX || y >= grid.resY || z >= grid.resZ) {
    return;
  }

  // Global linear index
  let idx = x + y * grid.resX + z * grid.resX * grid.resY;

  // Voxel center in world space
  let wx = grid.originX + (f32(x) + 0.5) * grid.voxelSize;
  let wy = grid.originY + (f32(y) + 0.5) * grid.voxelSize;
  let wz = grid.originZ + (f32(z) + 0.5) * grid.voxelSize;

  // Process all swept segments
  // CSG difference: result = max(stock, -tool)
  // -toolSDF is positive inside the tool, making those voxels "outside"
  var resultSDF = sdfData[idx];

  for (var i = 0u; i < grid.numPositions; i = i + 1u) {
    let base = i * 6u;
    let ax = positions[base + 0u];
    let ay = positions[base + 1u];
    let az = positions[base + 2u];
    let bx = positions[base + 3u];
    let by = positions[base + 4u];
    let bz = positions[base + 5u];

    let toolSDF = computeToolSDF(wx, wy, wz, ax, ay, az, bx, by, bz);
    resultSDF = max(resultSDF, -toolSDF);
  }

  // Write result (non-atomic, safe in sub-region dispatch)
  sdfData[idx] = resultSDF;
}
`;

export class SDFGPUCompute {
  private device: GPUDevice | null = null;
  private pipeline: GPUComputePipeline | null = null;
  private bindGroupLayout: GPUBindGroupLayout | null = null;

  private sdfBuffer: GPUBuffer | null = null;
  private uniformBuffer: GPUBuffer | null = null;
  private toolUniformBuffer: GPUBuffer | null = null;
  private positionsBuffer: GPUBuffer | null = null;
  private stagingBuffer: GPUBuffer | null = null;

  private resX = 0;
  private resY = 0;
  private resZ = 0;
  private voxelSize = 0;
  private origin: [number, number, number] = [0, 0, 0];
  private totalVoxels = 0;

  private initialized = false;

  // Pre-allocated uniform data (avoid GC)
  private uniformData = new ArrayBuffer(16 * 4); // 16 u32 fields
  private uniformView = new DataView(this.uniformData);
  private toolUniformData = new Float32Array(8); // ToolGeom struct
  private toolUniformU32View = new Uint32Array(this.toolUniformData.buffer); // For toolType field

  // Cached buffers
  private solidInitData: Float32Array | null = null;
  private cachedBindGroup: GPUBindGroup | null = null;
  private singleSweptPos = new Float32Array(6); // Pre-allocated for removeSweptGPU

  // Max batch size
  private readonly MAX_BATCH_POSITIONS = 256;

  constructor() {}

  /**
   * Initialize WebGPU device and allocate SDF buffer
   */
  async initialize(
    resX: number,
    resY: number,
    resZ: number,
    voxelSize: number,
    origin: [number, number, number]
  ): Promise<boolean> {
    try {
      if (!(navigator as any).gpu) {
        console.warn('[SDFGPUCompute] WebGPU not supported');
        return false;
      }

      const adapter = await (navigator as any).gpu.requestAdapter({ powerPreference: 'high-performance' });
      if (!adapter) {
        console.warn('[SDFGPUCompute] No WebGPU adapter found');
        return false;
      }

      this.resX = resX;
      this.resY = resY;
      this.resZ = resZ;
      this.voxelSize = voxelSize;
      this.origin = origin;
      this.totalVoxels = resX * resY * resZ;

      // Calculate required buffer size and request device with proper limits
      const sdfBufferSize = this.totalVoxels * 4; // 4 bytes per float
      const requiredMaxBuffer = Math.max(sdfBufferSize, 268435456); // At least default 256MB
      const adapterMaxBuffer = (adapter.limits as any).maxBufferSize || 268435456;
      const requestedMaxBuffer = Math.min(requiredMaxBuffer, adapterMaxBuffer);

      this.device = await adapter.requestDevice({
        requiredLimits: {
          maxBufferSize: requestedMaxBuffer,
          maxStorageBufferBindingSize: Math.min(
            Math.max(sdfBufferSize, adapter.limits.maxStorageBufferBindingSize),
            adapter.limits.maxStorageBufferBindingSize
          ),
        },
      });
      if ('lost' in this.device && typeof (this.device as any).lost?.then === 'function') {
        (this.device as any).lost.then((info: any) => {
          console.error('[SDFGPUCompute] Device lost:', info.message);
        });
      }

      // Check if buffer size exceeds device limits
      const deviceMaxBuffer = ((this.device as any).limits as any)?.maxBufferSize || 268435456;
      if (sdfBufferSize > deviceMaxBuffer) {
        console.error(`[SDFGPUCompute] Buffer size ${(sdfBufferSize / 1024 / 1024).toFixed(1)}MB exceeds device limit ${(deviceMaxBuffer / 1024 / 1024).toFixed(1)}MB. Reducing resolution.`);
        // Reduce resolution to fit within limits
        const maxVoxels = Math.floor(deviceMaxBuffer / 4);
        const scale = Math.cbrt(maxVoxels / this.totalVoxels);
        this.resX = Math.max(1, Math.floor(resX * scale));
        this.resY = Math.max(1, Math.floor(resY * scale));
        this.resZ = Math.max(1, Math.floor(resZ * scale));
        this.totalVoxels = this.resX * this.resY * this.resZ;
      }

      // Create SDF buffer (one f32 per voxel)
      const finalSdfBufferSize = this.totalVoxels * 4;
      this.sdfBuffer = this.device.createBuffer({
        size: finalSdfBufferSize,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
      });

      // Create uniform buffers
      this.uniformBuffer = this.device.createBuffer({
        size: 16 * 4, // 16 u32 fields
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });

      this.toolUniformBuffer = this.device.createBuffer({
        size: 8 * 4, // 8 f32 fields
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });

      // Create positions buffer (large enough for batch)
      this.positionsBuffer = this.device.createBuffer({
        size: this.MAX_BATCH_POSITIONS * 6 * 4, // 6 floats per segment
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      });

      // Create staging buffer for readback
      this.stagingBuffer = this.device.createBuffer({
        size: finalSdfBufferSize,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      // Create shader module and pipeline
      const shaderModule = this.device.createShaderModule({
        code: WGSL_SDF_SHADER,
      });

      this.bindGroupLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        ],
      });

      const pipelineLayout = this.device.createPipelineLayout({
        bindGroupLayouts: [this.bindGroupLayout],
      });

      this.pipeline = this.device.createComputePipeline({
        layout: pipelineLayout,
        compute: {
          module: shaderModule,
          entryPoint: 'main',
        },
      });

      // Initialize cached solidInitData buffer
      this.solidInitData = new Float32Array(this.totalVoxels);
      this.solidInitData.fill(-1000.0);

      this.initialized = true;
      // SDFGPUCompute initialized

      return true;

    } catch (error) {
      console.error('[SDFGPUCompute] Initialization failed:', error);
      return false;
    }
  }

  /**
   * Initialize all voxels with negative distance (solid stock)
   */
  initializeSolid(): void {
    if (!this.initialized || !this.device || !this.sdfBuffer || !this.solidInitData) {
      return;
    }

    // Use pre-allocated cached buffer (no GC allocation)
    this.device.queue.writeBuffer(this.sdfBuffer, 0, this.solidInitData);
  }

  /**
   * Compute sub-region AABB for tool sweep
   */
  private computeSubRegion(
    positions: Float32Array,
    numPositions: number,
    tool: VoxelToolGeometry
  ): {
    startX: number;
    startY: number;
    startZ: number;
    sizeX: number;
    sizeY: number;
    sizeZ: number;
  } | null {
    if (numPositions === 0) return null;

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    // Conservative AABB expansion: radius + full tool length (not half)
    const toolRadius = tool.diameter * 0.5 + tool.length;

    for (let i = 0; i < numPositions; i++) {
      const base = i * 6;
      const ax = positions[base + 0];
      const ay = positions[base + 1];
      const az = positions[base + 2];
      const bx = positions[base + 3];
      const by = positions[base + 4];
      const bz = positions[base + 5];

      minX = Math.min(minX, ax - toolRadius, bx - toolRadius);
      minY = Math.min(minY, ay - toolRadius, by - toolRadius);
      minZ = Math.min(minZ, az - toolRadius, bz - toolRadius);

      maxX = Math.max(maxX, ax + toolRadius, bx + toolRadius);
      maxY = Math.max(maxY, ay + toolRadius, by + toolRadius);
      maxZ = Math.max(maxZ, az + toolRadius, bz + toolRadius);
    }

    // Convert to voxel indices
    const startX = Math.max(0, Math.floor((minX - this.origin[0]) / this.voxelSize));
    const startY = Math.max(0, Math.floor((minY - this.origin[1]) / this.voxelSize));
    const startZ = Math.max(0, Math.floor((minZ - this.origin[2]) / this.voxelSize));

    const endX = Math.min(this.resX - 1, Math.ceil((maxX - this.origin[0]) / this.voxelSize));
    const endY = Math.min(this.resY - 1, Math.ceil((maxY - this.origin[1]) / this.voxelSize));
    const endZ = Math.min(this.resZ - 1, Math.ceil((maxZ - this.origin[2]) / this.voxelSize));

    const sizeX = Math.max(1, endX - startX + 1);
    const sizeY = Math.max(1, endY - startY + 1);
    const sizeZ = Math.max(1, endZ - startZ + 1);

    return { startX, startY, startZ, sizeX, sizeY, sizeZ };
  }

  /**
   * Batch remove: process swept segments within sub-region AABB
   */
  removeBatchGPU(
    positions: Float32Array,
    numPositions: number,
    tool: VoxelToolGeometry,
    dirX: number,
    dirY: number,
    dirZ: number
  ): void {
    if (!this.initialized || !this.device || !this.pipeline || !this.bindGroupLayout) {
      return;
    }

    if (numPositions === 0 || numPositions > this.MAX_BATCH_POSITIONS) {
      return;
    }

    // Compute sub-region
    const subRegion = this.computeSubRegion(positions, numPositions, tool);
    if (!subRegion) {
      return;
    }

    const { startX, startY, startZ, sizeX, sizeY, sizeZ } = subRegion;
    const totalSubVoxels = sizeX * sizeY * sizeZ;

    // Update uniform data (Grid struct)
    this.uniformView.setUint32(0, this.resX, true);
    this.uniformView.setUint32(4, this.resY, true);
    this.uniformView.setUint32(8, this.resZ, true);
    this.uniformView.setFloat32(12, this.voxelSize, true);

    this.uniformView.setFloat32(16, this.origin[0], true);
    this.uniformView.setFloat32(20, this.origin[1], true);
    this.uniformView.setFloat32(24, this.origin[2], true);
    this.uniformView.setUint32(28, numPositions, true);

    this.uniformView.setUint32(32, startX, true);
    this.uniformView.setUint32(36, startY, true);
    this.uniformView.setUint32(40, startZ, true);
    this.uniformView.setUint32(44, sizeX, true);

    this.uniformView.setUint32(48, sizeY, true);
    this.uniformView.setUint32(52, sizeZ, true);
    this.uniformView.setUint32(56, totalSubVoxels, true);
    const workgroupSize = 256; // Increased from 64 to 256
    const totalWorkgroups = Math.ceil(totalSubVoxels / workgroupSize);
    const MAX_WG = 65535;
    const workgroupsX = Math.min(totalWorkgroups, MAX_WG);
    const workgroupsY = Math.ceil(totalWorkgroups / MAX_WG);
    this.uniformView.setUint32(60, workgroupsX, true); // workgroupsX for 2D dispatch

    // Update tool uniform (ToolGeom struct)
    this.toolUniformData[0] = dirX;
    this.toolUniformData[1] = dirY;
    this.toolUniformData[2] = dirZ;
    this.toolUniformData[3] = 0; // padding

    this.toolUniformData[4] = tool.diameter;
    this.toolUniformData[5] = tool.cornerRadius || 0;
    this.toolUniformData[6] = tool.length;

    let toolType = 0; // flat
    if (tool.type === 'ball') toolType = 1;
    else if (tool.type === 'bull') toolType = 2;
    // CRITICAL FIX: Use Uint32Array view to avoid float/u32 bit pattern mismatch
    this.toolUniformU32View[7] = toolType;

    // Write buffers
    this.device.queue.writeBuffer(this.uniformBuffer!, 0, this.uniformData);
    this.device.queue.writeBuffer(this.toolUniformBuffer!, 0, this.toolUniformData);
    this.device.queue.writeBuffer(this.positionsBuffer!, 0, positions, 0, numPositions * 6);

    // Create cached bind group (only once, buffers never change)
    if (!this.cachedBindGroup) {
      this.cachedBindGroup = this.device.createBindGroup({
        layout: this.bindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: this.uniformBuffer! } },
          { binding: 1, resource: { buffer: this.toolUniformBuffer! } },
          { binding: 2, resource: { buffer: this.sdfBuffer! } },
          { binding: 3, resource: { buffer: this.positionsBuffer! } },
        ],
      });
    }

    // Dispatch compute shader
    const commandEncoder = this.device.createCommandEncoder();
    const passEncoder = commandEncoder.beginComputePass();
    passEncoder.setPipeline(this.pipeline);
    passEncoder.setBindGroup(0, this.cachedBindGroup);

    passEncoder.dispatchWorkgroups(workgroupsX, workgroupsY);

    passEncoder.end();
    this.device.queue.submit([commandEncoder.finish()]);
  }

  /**
   * Single swept removal (delegates to batch with 1 entry)
   */
  removeSweptGPU(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): void {
    // Use pre-allocated buffer (zero GC allocation)
    this.singleSweptPos[0] = ax;
    this.singleSweptPos[1] = ay;
    this.singleSweptPos[2] = az;
    this.singleSweptPos[3] = bx;
    this.singleSweptPos[4] = by;
    this.singleSweptPos[5] = bz;
    this.removeBatchGPU(this.singleSweptPos, 1, tool, dirX, dirY, dirZ);
  }

  /**
   * Read SDF data back from GPU as Float32Array
   */
  async readSDFData(): Promise<Float32Array | null> {
    if (!this.initialized || !this.device || !this.sdfBuffer || !this.stagingBuffer) {
      return null;
    }

    try {
      // Copy GPU buffer to staging buffer
      const commandEncoder = this.device.createCommandEncoder();
      commandEncoder.copyBufferToBuffer(
        this.sdfBuffer,
        0,
        this.stagingBuffer,
        0,
        this.totalVoxels * 4
      );
      this.device.queue.submit([commandEncoder.finish()]);

      // Wait for copy to complete
      if (typeof (this.device.queue as any).onSubmittedWorkDone === 'function') {
        await (this.device.queue as any).onSubmittedWorkDone();
      }

      // Map staging buffer
      await this.stagingBuffer.mapAsync(GPUMapMode.READ);
      const arrayBuffer = this.stagingBuffer.getMappedRange();
      const result = new Float32Array(arrayBuffer).slice(); // Copy data
      this.stagingBuffer.unmap();

      return result;

    } catch (error) {
      console.error('[SDFGPUCompute] Readback failed:', error);
      return null;
    }
  }

  /**
   * Flush GPU queue
   */
  async flush(): Promise<void> {
    if (!this.device) return;
    if (typeof (this.device.queue as any).onSubmittedWorkDone === 'function') {
      await (this.device.queue as any).onSubmittedWorkDone();
    }
  }

  /**
   * Getters for sharing with MarchingCubesGPU
   */
  getDevice(): GPUDevice | null {
    return this.device;
  }

  getSDFBuffer(): GPUBuffer | null {
    return this.sdfBuffer;
  }

  getGridInfo(): { resX: number; resY: number; resZ: number; voxelSize: number; origin: [number, number, number] } {
    return {
      resX: this.resX,
      resY: this.resY,
      resZ: this.resZ,
      voxelSize: this.voxelSize,
      origin: this.origin,
    };
  }

  getTotalVoxels(): number {
    return this.totalVoxels;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Dispose all GPU resources
   */
  dispose(): void {
    if (this.sdfBuffer) {
      this.sdfBuffer.destroy();
      this.sdfBuffer = null;
    }

    if (this.uniformBuffer) {
      this.uniformBuffer.destroy();
      this.uniformBuffer = null;
    }

    if (this.toolUniformBuffer) {
      this.toolUniformBuffer.destroy();
      this.toolUniformBuffer = null;
    }

    if (this.positionsBuffer) {
      this.positionsBuffer.destroy();
      this.positionsBuffer = null;
    }

    if (this.stagingBuffer) {
      this.stagingBuffer.destroy();
      this.stagingBuffer = null;
    }

    // Invalidate cached resources
    this.cachedBindGroup = null;
    this.solidInitData = null;

    this.pipeline = null;
    this.bindGroupLayout = null;
    this.device = null;
    this.initialized = false;

    // SDFGPUCompute disposed
  }
}

/**
 * Factory function: create and initialize SDFGPUCompute
 */
export async function createSDFGPUCompute(
  resX: number,
  resY: number,
  resZ: number,
  voxelSize: number,
  origin: [number, number, number]
): Promise<SDFGPUCompute | null> {
  const compute = new SDFGPUCompute();
  const success = await compute.initialize(resX, resY, resZ, voxelSize, origin);

  if (!success) {
    compute.dispose();
    return null;
  }

  return compute;
}
